import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { buildRandomRowQuestionTemplates, buildRowContextMessage } from '@/utils/buildRowQuestionTemplates';
import { storeAidaLaunchParams } from '@/utils/openAidaWithContext';
import { trackEvent } from '@/utils/analytics';
import './RowAidaQuestionsButton.css';

const POPOVER_CLOSE_EVENT = 'disgenet:close-row-aida-popover';

interface RowAidaQuestionsButtonProps {
  row: Record<string, unknown>;
}

export default function RowAidaQuestionsButton({ row }: RowAidaQuestionsButtonProps) {
  const { groups, isAuthenticated } = userAppContext();
  const isAcademicUser = Boolean(isAuthenticated && groups?.includes('ROLE_ACADEMIC'));
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);

  const contextMessage = buildRowContextMessage(row);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.left - 8,
    });
  }, []);

  const closePopover = useCallback(() => setIsOpen(false), []);

  const openPopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent(POPOVER_CLOSE_EVENT));
    updatePosition();
    setQuestions(buildRandomRowQuestionTemplates(row));
    setIsOpen(true);
    trackEvent({
      name: 'row_aida_questions_open',
      buttonId: 'table_open_row_aida_questions',
      label: 'Open row AIDA questions',
      page: 'search_results',
      section: 'results_table',
      feature: 'aida',
      eventCategory: 'table',
    });
  };

  const handleQuestionClick = (question: string) => {
    if (isAcademicUser) return;

    closePopover();
    trackEvent({
      name: 'row_aida_question_select',
      buttonId: 'table_row_aida_question',
      label: 'Row AIDA suggested question',
      page: 'search_results',
      section: 'results_table',
      feature: 'aida',
      eventCategory: 'table',
    });
    storeAidaLaunchParams({ question, autoSend: true });
    navigate('/Assistant');
  };

  const handleOpenChatWithContext = () => {
    if (isAcademicUser) return;

    closePopover();
    trackEvent({
      name: 'row_aida_open_chat',
      buttonId: 'table_row_aida_open_chat',
      label: 'Open AIDA with row context',
      page: 'search_results',
      section: 'results_table',
      feature: 'aida',
      eventCategory: 'table',
    });
    storeAidaLaunchParams({ question: contextMessage, autoSend: false });
    navigate('/Assistant');
  };

  useEffect(() => {
    const onCloseOthers = () => closePopover();
    window.addEventListener(POPOVER_CLOSE_EVENT, onCloseOthers);
    return () => window.removeEventListener(POPOVER_CLOSE_EVENT, onCloseOthers);
  }, [closePopover]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      closePopover();
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, closePopover, updatePosition]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={openPopover}
        className="tab-links pl-0.5 pr-2"
        title="Ask AIDA about this row"
        aria-label="Ask AIDA about this row"
        aria-expanded={isOpen}
      >
        <ChatBubbleLeftIcon className="w-4 h-4" strokeWidth={2} style={{ color: '#D02591' }} />
      </button>

      {isOpen &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            className="row-aida-questions-popover"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              transform: 'translate(-100%, -50%)',
            }}
            role="dialog"
            aria-label="Suggested questions for this row"
            onClick={e => e.stopPropagation()}
          >
            <p className="px-2 pt-2 font-semibold text-pink">Ask AIDA about this row</p>
            {isAcademicUser && (
              <p className="row-aida-questions-popover__upgrade">
                Upgrade your plan to access AIDA suggested questions.{' '}
                <a href="/plans" className="row-aida-questions-popover__upgrade-link">
                  View plans
                </a>
              </p>
            )}
            <ul className="row-aida-questions-popover__list">
              {questions.map((question, index) => (
                <li key={`${index}-${question}`}>
                  <button
                    type="button"
                    className="row-aida-questions-popover__question"
                    onClick={() => handleQuestionClick(question)}
                    disabled={isAcademicUser}
                    aria-disabled={isAcademicUser}
                  >
                    {question}
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="row-aida-questions-popover__footer"
              onClick={handleOpenChatWithContext}
              disabled={isAcademicUser}
              aria-disabled={isAcademicUser}
            >
              Or open the chat with this context → Ask something else
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
